import { useEffect, useMemo, useRef, useState } from 'react';
import { getJSON, NOT_MODIFIED } from '../../lib/apiClient';
import { fetchItemCategories } from '../../lib/kdsApi';
import { useI18n } from '../../lib/i18n';
import type { KdsTicket } from '../../types/kds';
import type { ServiceFilters } from '../../types/service';

// ─────────────────────────────────────────────────────────────────────────────
// Filtering the kitchen board.
//
// The legacy Service Manager has four filters (branch, order type, date,
// categories). Only two of them mean anything on a live kitchen board, and
// pretending otherwise would make the screen worse, not more familiar:
//
//   Order type   REAL. "Show me only delivery" is a question a pass asks.
//   Categories   REAL, though it overlaps with the station pills — stations
//                are defined BY category routing. Kept because a single-screen
//                kitchen has no stations to click.
//   Branch       NOT APPLICABLE. A KDS session is bound to one branch: the
//                board query is branch-scoped and bumping another branch's
//                kitchen is refused server-side. The branch is shown in the
//                header instead of offered as a filter that cannot move.
//   Date         NOT APPLICABLE. The board holds tickets that are open NOW.
//                A date picker over it can only ever return "today" or nothing.
//
// FILTERS ARE NEVER PERSISTED. That is the important decision in this file. A
// filter left on overnight is a board that silently hides half the orders, and
// the person who finds it is the one who has just been shouted at for a missing
// dish. They reset on reload, and while any is active the bar is highlighted
// with a one-tap Clear.
// ─────────────────────────────────────────────────────────────────────────────

export interface BoardFilterState {
  orderType: 'all' | 'dine-in' | number;
  categoryIds: number[];
  search: string;
}

export const EMPTY_FILTERS: BoardFilterState = {
  orderType: 'all',
  categoryIds: [],
  search: '',
};

export function isFiltering(f: BoardFilterState): boolean {
  return f.orderType !== 'all' || f.categoryIds.length > 0 || f.search.trim() !== '';
}

/**
 * Apply the filters to a board.
 *
 * `itemCategories` may be empty (still loading, or the request failed) — in
 * that case a category filter cannot be evaluated, so it is IGNORED rather than
 * applied. Hiding every ticket because a lookup has not arrived is the failure
 * mode this screen can least afford.
 */
export function filterTickets(
  tickets: KdsTicket[],
  filters: BoardFilterState,
  itemCategories: Map<number, number[]>,
): KdsTicket[] {
  const needle = filters.search.trim().toLowerCase();
  const wantCats = filters.categoryIds.length > 0 && itemCategories.size > 0
    ? new Set(filters.categoryIds)
    : null;

  return tickets.filter((ticket) => {
    if (filters.orderType === 'dine-in') {
      if (ticket.orderType !== null) return false;
    } else if (filters.orderType !== 'all') {
      if (ticket.orderType !== filters.orderType) return false;
    }

    if (wantCats) {
      const hit = ticket.lines.some((line) => {
        if (line.itemId == null) return false;
        const cats = itemCategories.get(line.itemId);
        return cats ? cats.some((c) => wantCats.has(c)) : false;
      });
      if (!hit) return false;
    }

    if (needle) {
      const hay = [
        String(ticket.orderNo ?? ''),
        ticket.orderCode ?? '',
        ticket.tableLabel ?? '',
        ticket.customerName ?? '',
        ticket.customerNameAr ?? '',
        ...ticket.lines.flatMap((l) => [l.name ?? '', l.nameAr ?? '']),
      ];
      if (!hay.some((h) => h.toLowerCase().includes(needle))) return false;
    }

    return true;
  });
}

/** Loads the two lookups the bar needs. Both are best-effort. */
export function useFilterLookups(): {
  options: ServiceFilters | null;
  itemCategories: Map<number, number[]>;
} {
  const [options, setOptions] = useState<ServiceFilters | null>(null);
  const [itemCategories, setItemCategories] = useState<Map<number, number[]>>(new Map());

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        // Reuses the Service Manager's lookup endpoint rather than adding a
        // second one: same auth, same tenant, same three lists.
        const result = await getJSON<ServiceFilters>('/api/v1/service/filters');
        if (alive && result !== NOT_MODIFIED) setOptions(result);
      } catch {
        // No filter options means the bar shows order types it derives from the
        // board itself. Not worth an error banner on a kitchen screen.
      }
      try {
        const result = await fetchItemCategories();
        if (alive && result !== NOT_MODIFIED) setItemCategories(new Map(result.items));
      } catch {
        // See filterTickets: with no map the category filter is skipped.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return { options, itemCategories };
}

// ── The bar ──────────────────────────────────────────────────────────────────

export interface BoardFiltersProps {
  filters: BoardFilterState;
  options: ServiceFilters | null;
  /** Order types actually present on the board, so we never offer an empty one. */
  presentOrderTypes: Array<number | null>;
  onChange: (next: BoardFilterState) => void;
}

export function BoardFilters({
  filters,
  options,
  presentOrderTypes,
  onChange,
}: BoardFiltersProps): JSX.Element {
  const { lang, t } = useI18n();
  const active = isFiltering(filters);

  const orderTypes = useMemo(() => {
    const present = new Set(presentOrderTypes.map((v) => (v === null ? 'dine-in' : v)));
    return (options?.orderTypes ?? []).filter((ot) =>
      present.has(ot.value === null ? 'dine-in' : ot.value),
    );
  }, [options, presentOrderTypes]);

  const categories = options?.categories ?? [];

  return (
    <div className={`kds-filters ${active ? 'active' : ''}`}>
      <label className="kds-filter-search">
        <span aria-hidden="true">🔍</span>
        <input
          type="search"
          value={filters.search}
          placeholder={t('search')}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
        />
      </label>

      {orderTypes.length > 1 && (
        <div className="kds-filter-types">
          <button
            type="button"
            className={`kds-filter-pill ${filters.orderType === 'all' ? 'active' : ''}`}
            onClick={() => onChange({ ...filters, orderType: 'all' })}
          >
            {t('allTypes')}
          </button>
          {orderTypes.map((ot) => {
            const value = ot.value === null ? ('dine-in' as const) : ot.value;
            return (
              <button
                key={String(ot.value)}
                type="button"
                className={`kds-filter-pill ${filters.orderType === value ? 'active' : ''}`}
                onClick={() => onChange({ ...filters, orderType: value })}
              >
                {lang === 'ar' ? ot.labelAr || ot.label : ot.label}
              </button>
            );
          })}
        </div>
      )}

      {categories.length > 0 && (
        <CategoryPicker
          all={categories.map((c) => ({
            id: c.id,
            name: lang === 'ar' ? c.nameAr || c.name : c.name,
          }))}
          selected={filters.categoryIds}
          onChange={(categoryIds) => onChange({ ...filters, categoryIds })}
        />
      )}

      {active && (
        <button
          type="button"
          className="kds-filter-clear"
          onClick={() => onChange({ ...EMPTY_FILTERS })}
        >
          ✕ {t('clear')}
        </button>
      )}
    </div>
  );
}

function CategoryPicker(props: {
  all: Array<{ id: number; name: string }>;
  selected: number[];
  onChange: (next: number[]) => void;
}): JSX.Element {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const selectedSet = new Set(props.selected);
  const label =
    props.selected.length === 0
      ? t('allCategories')
      : `${props.selected.length} ${t('selected')}`;

  return (
    <div className="kds-filter-cats" ref={wrapRef}>
      <button
        type="button"
        className={`kds-filter-pill ${props.selected.length > 0 ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {label} ▾
      </button>
      {open && (
        <div className="kds-filter-panel" role="listbox">
          <div className="kds-filter-panel-head">
            <span>{t('categories')}</span>
            <button
              type="button"
              className="kds-filter-panel-clear"
              onClick={() => props.onChange([])}
            >
              {t('clear')}
            </button>
          </div>
          <div className="kds-filter-options">
            {props.all.map((c) => (
              <label key={c.id} className="kds-filter-option" title={c.name}>
                <input
                  type="checkbox"
                  checked={selectedSet.has(c.id)}
                  onChange={() => {
                    const next = new Set(selectedSet);
                    if (next.has(c.id)) next.delete(c.id);
                    else next.add(c.id);
                    props.onChange([...next]);
                  }}
                />
                <span>{c.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
